import { useGlobalProperty } from '@/hooks/useGlobalProperty';
import React, { useState, useEffect } from 'react';
import DashboardLayout from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { propertyAPI } from '@/services/api';
import { DollarSign, Clock, AlertTriangle, CheckCircle, CreditCard, CalendarClock, User, Building, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_STYLES = {
  pendiente: { label: 'Al día', className: 'bg-blue-100 text-blue-700', icon: Clock },
  vencido: { label: 'Vencido', className: 'bg-red-100 text-red-700', icon: AlertTriangle },
  pagado: { label: 'Pagado', className: 'bg-green-100 text-green-700', icon: CheckCircle },
};

export default function CuentasPorCobrarPage() {
  const [properties, setProperties] = useState([]);
  const [selectedProperty, setSelectedProperty] = useGlobalProperty(properties);
  const [credits, setCredits] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('todos');
  const [payDialogOpen, setPayDialogOpen] = useState(false);
  const [selectedCredit, setSelectedCredit] = useState(null);
  const [payAmount, setPayAmount] = useState('');
  const [payMethod, setPayMethod] = useState('efectivo');
  const [payNote, setPayNote] = useState('');
  const [paying, setPaying] = useState(false);

  useEffect(() => { fetchProperties(); }, []);
  useEffect(() => { if (selectedProperty) fetchData(); }, [selectedProperty]);

  const fetchProperties = async () => {
    try {
      const res = await propertyAPI.getAll();
      setProperties(res.data);
      
    } catch (e) { toast.error('Error al cargar propiedades'); }
    finally { setLoading(false); }
  };

  const fetchData = async () => {
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };
    try {
      const [creditsRes, statsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/credits?property_id=${selectedProperty}`, { headers }),
        axios.get(`${BACKEND_URL}/api/credits/stats?property_id=${selectedProperty}`, { headers }),
      ]);
      setCredits(creditsRes.data);
      setStats(statsRes.data);
    } catch (e) { toast.error('Error al cargar créditos'); }
  };

  const handlePay = async (e) => {
    e.preventDefault();
    if (!selectedCredit || !payAmount) return;
    setPaying(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${BACKEND_URL}/api/credits/${selectedCredit.id}/pay`,
        { monto_pago: parseFloat(payAmount), metodo_pago: payMethod, nota: payNote },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(res.data.message);
      setPayDialogOpen(false);
      setSelectedCredit(null);
      setPayAmount('');
      setPayNote('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar pago');
    } finally { setPaying(false); }
  };

  const openPayDialog = (credit) => {
    const restante = credit.monto - (credit.monto_pagado || 0);
    setSelectedCredit(credit);
    setPayAmount(restante.toString());
    setPayDialogOpen(true);
  };

  const filtered = credits.filter(c => filter === 'todos' || c.estado === filter);

  const formatDate = (iso) => {
    if (!iso) return '-';
    return new Date(iso).toLocaleDateString('es-CO', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  const daysUntil = (iso) => {
    if (!iso) return 0;
    return Math.ceil((new Date(iso) - new Date()) / (1000 * 60 * 60 * 24));
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="cuentas-por-cobrar-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold" style={{fontFamily: 'Manrope'}}>Cuentas por Cobrar</h1>
            <p className="text-muted-foreground mt-1">Gestión de créditos y cobros pendientes</p>
          </div>
          {properties.length > 0 && (
            <Select value={selectedProperty} onValueChange={setSelectedProperty}>
              <SelectTrigger className="w-[220px]" data-testid="cobrar-property-selector">
                <SelectValue placeholder="Propiedad" />
              </SelectTrigger>
              <SelectContent>
                {properties.map((p) => (
                  <SelectItem key={p.id} value={p.id}>{p.nombre}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            <Card className="border-l-4 border-l-amber-500" data-testid="stat-total-por-cobrar">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                    <DollarSign className="h-5 w-5 text-amber-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">${stats.total_por_cobrar?.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Total por Cobrar</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-blue-500 cursor-pointer hover:bg-blue-50/50 transition-colors" onClick={() => setFilter('pendiente')} data-testid="stat-al-dia">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                    <Clock className="h-5 w-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">${stats.al_dia?.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Al Día ({stats.count_pendientes})</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-red-500 cursor-pointer hover:bg-red-50/50 transition-colors" onClick={() => setFilter('vencido')} data-testid="stat-vencidos">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                    <AlertTriangle className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">${stats.vencidos?.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Vencidos ({stats.count_vencidos})</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-green-500 cursor-pointer hover:bg-green-50/50 transition-colors" onClick={() => setFilter('pagado')} data-testid="stat-cobrado">
              <CardContent className="p-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">${stats.cobrado_total?.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Cobrado ({stats.count_pagados})</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="flex gap-2">
          {[
            { key: 'todos', label: 'Todos' },
            { key: 'pendiente', label: 'Al Día' },
            { key: 'vencido', label: 'Vencidos' },
            { key: 'pagado', label: 'Pagados' },
          ].map((f) => (
            <Button
              key={f.key}
              size="sm"
              variant={filter === f.key ? 'default' : 'outline'}
              onClick={() => setFilter(f.key)}
              data-testid={`filter-${f.key}`}
            >
              {f.label}
            </Button>
          ))}
        </div>

        {/* Credits Table */}
        <Card>
          <CardHeader>
            <CardTitle style={{fontFamily: 'Manrope'}}>
              Créditos {filter !== 'todos' ? `(${filter})` : ''} ({filtered.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {filtered.length === 0 ? (
              <div className="text-center py-12">
                <Receipt className="h-12 w-12 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No hay créditos {filter !== 'todos' ? `con estado "${filter}"` : 'registrados'}</p>
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map((credit) => {
                  const restante = credit.monto - (credit.monto_pagado || 0);
                  const days = daysUntil(credit.fecha_vencimiento);
                  const statusInfo = STATUS_STYLES[credit.estado] || STATUS_STYLES.pendiente;
                  const StatusIcon = statusInfo.icon;
                  const isUrgent = credit.estado === 'pendiente' && days <= 3 && days >= 0;

                  return (
                    <div
                      key={credit.id}
                      className={`p-4 border rounded-lg transition-colors ${
                        credit.estado === 'vencido' ? 'border-red-200 bg-red-50/30' :
                        isUrgent ? 'border-amber-200 bg-amber-50/30' :
                        credit.estado === 'pagado' ? 'border-green-200 bg-green-50/30' :
                        'hover:bg-muted/30'
                      }`}
                      data-testid={`credit-row-${credit.id}`}
                    >
                      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
                        {/* Client & Reservation Info */}
                        <div className="flex items-start gap-3 min-w-0 flex-1">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                            credit.estado === 'vencido' ? 'bg-red-100' : credit.estado === 'pagado' ? 'bg-green-100' : 'bg-blue-100'
                          }`}>
                            <StatusIcon className={`h-5 w-5 ${
                              credit.estado === 'vencido' ? 'text-red-600' : credit.estado === 'pagado' ? 'text-green-600' : 'text-blue-600'
                            }`} />
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="font-semibold truncate">{credit.client_nombre}</p>
                              <Badge className={statusInfo.className} variant="outline">{statusInfo.label}</Badge>
                              {isUrgent && (
                                <Badge className="bg-amber-100 text-amber-700 animate-pulse" variant="outline">
                                  Vence en {days} día{days !== 1 ? 's' : ''}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-muted-foreground">
                              Huésped: {credit.guest_name || '-'} &middot; Plazo: {credit.plazo_dias} días
                            </p>
                            {credit.reservation_info?.fecha_checkin && (
                              <p className="text-xs font-medium text-blue-700 mt-0.5">
                                Reserva: {credit.reservation_info.room_name || 'Habitación'} &middot; {formatDate(credit.reservation_info.fecha_checkin)} → {formatDate(credit.reservation_info.fecha_checkout)} &middot; <span className="font-mono text-[10px] bg-blue-50 px-1 rounded">ID: {credit.reservation_id?.slice(0, 8)}</span>
                              </p>
                            )}
                            <p className="text-xs text-muted-foreground">
                              Creado: {formatDate(credit.created_at)} &middot; Vence: {formatDate(credit.fecha_vencimiento)}
                            </p>
                          </div>
                        </div>

                        {/* Amounts */}
                        <div className="flex items-center gap-4 flex-shrink-0">
                          <div className="text-right">
                            <p className="text-lg font-bold">${credit.monto?.toLocaleString()}</p>
                            <p className="text-xs text-muted-foreground">Monto Total</p>
                          </div>
                          {credit.monto_pagado > 0 && (
                            <div className="text-right">
                              <p className="text-sm font-medium text-green-600">${credit.monto_pagado?.toLocaleString()}</p>
                              <p className="text-xs text-muted-foreground">Abonado</p>
                            </div>
                          )}
                          {credit.estado !== 'pagado' && (
                            <div className="text-right">
                              <p className={`text-sm font-bold ${credit.estado === 'vencido' ? 'text-red-600' : 'text-amber-600'}`}>
                                ${restante?.toLocaleString()}
                              </p>
                              <p className="text-xs text-muted-foreground">Pendiente</p>
                            </div>
                          )}
                          {credit.estado !== 'pagado' && (
                            <Button
                              size="sm"
                              className="bg-slate-800 hover:bg-slate-700"
                              onClick={() => openPayDialog(credit)}
                              data-testid={`pay-credit-${credit.id}`}
                            >
                              <DollarSign className="h-4 w-4 mr-1" />
                              Cobrar
                            </Button>
                          )}
                        </div>
                      </div>

                      {/* Payment History */}
                      {credit.pagos && credit.pagos.length > 0 && (
                        <div className="mt-3 pt-3 border-t border-dashed">
                          <p className="text-xs font-medium text-muted-foreground mb-1">Historial de pagos:</p>
                          <div className="flex flex-wrap gap-2">
                            {credit.pagos.map((p, i) => (
                              <Badge key={i} variant="outline" className="text-xs bg-green-50">
                                ${p.monto?.toLocaleString()} - {p.metodo} ({formatDate(p.fecha)})
                              </Badge>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pay Dialog */}
        <Dialog open={payDialogOpen} onOpenChange={setPayDialogOpen}>
          <DialogContent className="max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-green-600" />
                Registrar Cobro
              </DialogTitle>
            </DialogHeader>
            {selectedCredit && (
              <form onSubmit={handlePay} className="space-y-4">
                <div className="bg-muted/50 rounded-lg p-3 space-y-1">
                  <p className="text-sm"><strong>Cliente:</strong> {selectedCredit.client_nombre}</p>
                  <p className="text-sm"><strong>Monto Total:</strong> ${selectedCredit.monto?.toLocaleString()}</p>
                  <p className="text-sm"><strong>Pagado:</strong> ${(selectedCredit.monto_pagado || 0)?.toLocaleString()}</p>
                  <p className="text-sm font-bold"><strong>Pendiente:</strong> ${(selectedCredit.monto - (selectedCredit.monto_pagado || 0))?.toLocaleString()}</p>
                </div>
                <div className="space-y-2">
                  <Label>Monto del Pago ($)</Label>
                  <Input
                    type="number"
                    min="1"
                    max={selectedCredit.monto - (selectedCredit.monto_pagado || 0)}
                    value={payAmount}
                    onChange={(e) => setPayAmount(e.target.value)}
                    required
                    data-testid="pay-amount-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Método de Pago</Label>
                  <Select value={payMethod} onValueChange={setPayMethod}>
                    <SelectTrigger data-testid="pay-method-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="efectivo">Efectivo</SelectItem>
                      <SelectItem value="transferencia">Transferencia</SelectItem>
                      <SelectItem value="zelle">Zelle</SelectItem>
                      <SelectItem value="tarjeta_debito">Tarjeta Débito</SelectItem>
                      <SelectItem value="tarjeta_credito">Tarjeta Crédito</SelectItem>
                      <SelectItem value="pago_movil">Pago Móvil</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Nota (opcional)</Label>
                  <Input
                    placeholder="Referencia o nota del pago"
                    value={payNote}
                    onChange={(e) => setPayNote(e.target.value)}
                    data-testid="pay-note-input"
                  />
                </div>
                <div className="flex gap-2 justify-end">
                  <Button type="button" variant="outline" onClick={() => setPayDialogOpen(false)}>Cancelar</Button>
                  <Button type="submit" className="bg-slate-800 hover:bg-slate-700" disabled={paying} data-testid="confirm-pay-btn">
                    {paying ? 'Procesando...' : 'Registrar Cobro'}
                  </Button>
                </div>
              </form>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
